import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import AdminDashboardLayout from './AdminDashboardLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
    getAdminSubscriptionsAPI,
    getAdminSubscriptionStatsAPI,
    grantFreeSubscriptionAPI,
    searchUsersForSubscriptionAPI,
    clearSearchUsers
} from '../../redux/slices/adminSubscriptionSlice';
import { base } from '../../constants/base';
import { toast } from 'react-toastify';
import AdminDataTable from '../../components/AdminComponents/AdminDataTable';
import './AdminSubscriptions.css';

const AdminSubscriptions = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.adminSubscriptions.loading);
    const statsLoading = useSelector(state => state.adminSubscriptions.statsLoading);
    const subscriptions = useSelector(state => state.adminSubscriptions.subscriptions);
    const stats = useSelector(state => state.adminSubscriptions.stats);
    const pagination = useSelector(state => state.adminSubscriptions.pagination);
    const grantLoading = useSelector(state => state.adminSubscriptions.grantLoading);
    const searchUsers = useSelector(state => state.adminSubscriptions.searchUsers);
    const searchLoading = useSelector(state => state.adminSubscriptions.searchLoading);

    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [days, setDays] = useState('');
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimerRef = useRef(null);
    const dropdownRef = useRef(null);

    const fetchSubscriptions = (page = currentPage, search = '') => {
        const payload = {
            page: page,
            search: search,
            status: statusFilter,
            type: typeFilter
        };
        dispatch(getAdminSubscriptionsAPI(payload));
    };

    const fetchStats = () => {
        dispatch(getAdminSubscriptionStatsAPI());
    };

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchSubscriptions(currentPage);
    }, [currentPage, statusFilter, typeFilter]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (searchValue) => {
        setCurrentPage(1);
        fetchSubscriptions(1, searchValue);
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleTypeFilter = (e) => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    // Modal handlers
    const openModal = () => {
        setShowModal(true);
        setSelectedUser(null);
        setDays('');
        setUserSearchQuery('');
        dispatch(clearSearchUsers());
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setDays('');
        setUserSearchQuery('');
        setShowDropdown(false);
        dispatch(clearSearchUsers());
    };

    const handleUserSearch = useCallback((e) => {
        const value = e.target.value;
        setUserSearchQuery(value);
        setSelectedUser(null);

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        if (value.trim().length >= 2) {
            searchTimerRef.current = setTimeout(() => {
                dispatch(searchUsersForSubscriptionAPI(value));
                setShowDropdown(true);
            }, 400);
        } else {
            dispatch(clearSearchUsers());
            setShowDropdown(false);
        }
    }, [dispatch]);

    const selectUser = (user) => {
        setSelectedUser(user);
        setUserSearchQuery(user.name);
        setShowDropdown(false);
        dispatch(clearSearchUsers());
    };

    const handleQuickSelect = (value) => {
        setDays(value.toString());
    };

    const handleGrantFree = async () => {
        if (!selectedUser) {
            toast.error('Please select a user');
            return;
        }
        if (!days || parseInt(days) < 1) {
            toast.error('Please enter valid number of days');
            return;
        }

        const res = await dispatch(grantFreeSubscriptionAPI({
            user_id: selectedUser.id,
            days: parseInt(days)
        }));

        if (res.meta.requestStatus === 'fulfilled') {
            toast.success(`Free subscription of ${days} days granted to ${selectedUser.name}`);
            closeModal();
            fetchSubscriptions(1);
            fetchStats();
            setCurrentPage(1);
        } else {
            toast.error(res.payload?.message || 'Failed to grant free subscription');
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'status-active';
            case 'cancelled':
                return 'status-cancelled';
            case 'expired':
                return 'status-expired';
            default:
                return 'status-pending';
        }
    };

    const getTypeBadgeClass = (type) => {
        switch (type?.toLowerCase()) {
            case 'user':
                return 'type-user';
            case 'corporate':
                return 'type-corporate';
            case 'professional':
                return 'type-professional';
            default:
                return 'type-default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const statsCards = [
        { title: 'Total Subscriptions', value: stats?.total_subscriptions || 0, icon: 'fa-solid fa-credit-card', color: '#EF4444' },
        { title: 'Active', value: stats?.active_subscriptions || 0, icon: 'fa-solid fa-check-circle', color: '#60A5FA' },
        { title: 'Cancelled', value: stats?.cancelled_subscriptions || 0, icon: 'fa-solid fa-times-circle', color: '#4ADE80' },
        { title: 'Expired', value: stats?.expired_subscriptions || 0, icon: 'fa-solid fa-clock', color: '#FBBF24' },
    ];

    const quickSelectOptions = [
        { label: '30 Days', value: 30 },
        { label: '90 Days', value: 90 },
        { label: '180 Days', value: 180 },
        { label: '1 Year', value: 365 },
        { label: '2 Years', value: 730 },
    ];

    const columns = useMemo(() => [
        {
            Header: 'Subscriber',
            accessor: 'user',
            Cell: ({ row }) => (
                <div className="admin-dt-user-cell">
                    {row.original?.user?.images?.[0]?.path ? (
                        <img
                            src={`${base.imageUrl}${row.original.user.images[0].path}`}
                            alt={row.original?.user?.name}
                            className="admin-dt-user-avatar"
                        />
                    ) : (
                        <div className="admin-dt-user-placeholder">
                            <i className="fas fa-user"></i>
                        </div>
                    )}
                    <div className="admin-dt-user-info">
                        <p className="admin-dt-user-name">{row.original?.user?.name || '-'}</p>
                        <p className="admin-dt-user-email">{row.original?.user?.email || '-'}</p>
                    </div>
                </div>
            ),
        },
        {
            Header: 'Plan',
            accessor: 'name',
            Cell: ({ value }) => value || '-',
        },
        {
            Header: 'Type',
            accessor: 'type',
            className: 'text-center',
            Cell: ({ value }) => (
                <span className={`subscription-type-badge ${getTypeBadgeClass(value)}`}>
                    {value || '-'}
                </span>
            ),
        },
        {
            Header: 'Start Date',
            accessor: 'start_date',
            className: 'text-center',
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: 'End Date',
            accessor: 'end_date',
            className: 'text-center',
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: 'Status',
            accessor: 'status',
            className: 'text-center',
            Cell: ({ value }) => (
                <span className={`subscription-status-badge ${getStatusBadgeClass(value)}`}>
                    {value || '-'}
                </span>
            ),
        },
    ], []);

    return (
        <AdminDashboardLayout>
            <div className="admin-subscriptions">
                <div className="admin-page-header">
                    <h1 className="admin-page-title">Subscriptions</h1>
                    <button className="grant-free-btn" onClick={openModal}>
                        <i className="fa-solid fa-gift"></i>
                        Grant Free Subscription
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="subscription-stats-grid">
                    {statsCards.map((card, index) => (
                        <div key={index} className="subscription-stat-card">
                            <div className="subscription-stat-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                                <i className={card.icon}></i>
                            </div>
                            <div className="subscription-stat-content">
                                <p className="subscription-stat-label">{card.title}</p>
                                {statsLoading ? (
                                    <div className="subscription-stat-loader"></div>
                                ) : (
                                    <h2 className="subscription-stat-value">{card.value}</h2>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="subscription-filters">
                    <div className="subscription-filter-group">
                        <label>Status</label>
                        <select value={statusFilter} onChange={handleStatusFilter} className="subscription-filter-select">
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>
                    <div className="subscription-filter-group">
                        <label>Type</label>
                        <select value={typeFilter} onChange={handleTypeFilter} className="subscription-filter-select">
                            <option value="">All Types</option>
                            <option value="user">User</option>
                            <option value="corporate">Corporate</option>
                            <option value="professional">Professional</option>
                        </select>
                    </div>
                </div>

                {/* Data Table */}
                <AdminDataTable
                    columns={columns}
                    data={subscriptions}
                    title="All Subscriptions"
                    icon="fa-solid fa-credit-card"
                    loading={loading}
                    searchable={true}
                    searchPlaceholder="Search subscriptions..."
                    onSearch={handleSearch}
                    serverSidePagination={{
                        currentPage: pagination?.current_page || 1,
                        totalPages: pagination?.total_pages || 1,
                        total: pagination?.total || 0,
                        perPage: pagination?.per_page || 10,
                        onPageChange: handlePageChange,
                    }}
                />

                {/* Grant Free Subscription Modal */}
                {showModal && (
                    <div className="grant-modal-overlay" onClick={closeModal}>
                        <div className="grant-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="grant-modal-header">
                                <h2>Grant Free Subscription</h2>
                                <button className="grant-modal-close" onClick={closeModal}>
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>

                            <div className="grant-modal-body">
                                {/* User Search */}
                                <div className="grant-form-group">
                                    <label>Select User</label>
                                    <div className="grant-user-search" ref={dropdownRef}>
                                        <div className="grant-search-input-wrapper">
                                            <i className="fa-solid fa-magnifying-glass"></i>
                                            <input
                                                type="text"
                                                value={userSearchQuery}
                                                onChange={handleUserSearch}
                                                placeholder="Search by name or email..."
                                                className="grant-search-input"
                                            />
                                            {searchLoading && <div className="grant-search-spinner"></div>}
                                        </div>

                                        {showDropdown && searchUsers.length > 0 && (
                                            <div className="grant-user-dropdown">
                                                {searchUsers.map((user) => (
                                                    <div
                                                        key={user.id}
                                                        className="grant-user-option"
                                                        onClick={() => selectUser(user)}
                                                    >
                                                        <div className="grant-user-option-avatar">
                                                            {user.images?.[0]?.path ? (
                                                                <img src={`${base.imageUrl}${user.images[0].path}`} alt={user.name} />
                                                            ) : (
                                                                <i className="fas fa-user"></i>
                                                            )}
                                                        </div>
                                                        <div className="grant-user-option-info">
                                                            <p className="grant-user-option-name">{user.name}</p>
                                                            <p className="grant-user-option-email">{user.email}</p>
                                                        </div>
                                                        <span className={`grant-user-option-role ${user.role?.name}`}>
                                                            {user.role?.name}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {showDropdown && !searchLoading && searchUsers.length === 0 && userSearchQuery.length >= 2 && (
                                            <div className="grant-user-dropdown">
                                                <div className="grant-user-no-results">No users found</div>
                                            </div>
                                        )}
                                    </div>

                                    {selectedUser && (
                                        <div className="grant-selected-user">
                                            <i className="fa-solid fa-circle-check"></i>
                                            <span>{selectedUser.name} ({selectedUser.email})</span>
                                        </div>
                                    )}
                                </div>

                                {/* Days Input */}
                                <div className="grant-form-group">
                                    <label>Number of Free Days</label>
                                    <input
                                        type="number"
                                        value={days}
                                        onChange={(e) => setDays(e.target.value)}
                                        placeholder="Enter number of days..."
                                        className="grant-days-input"
                                        min="1"
                                    />
                                </div>

                                {/* Quick Select */}
                                <div className="grant-form-group">
                                    <label>Quick Select</label>
                                    <div className="grant-quick-select">
                                        {quickSelectOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`grant-quick-btn ${parseInt(days) === option.value ? 'active' : ''}`}
                                                onClick={() => handleQuickSelect(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grant-modal-footer">
                                <button className="grant-cancel-btn" onClick={closeModal}>
                                    Cancel
                                </button>
                                <button
                                    className="grant-submit-btn"
                                    onClick={handleGrantFree}
                                    disabled={grantLoading || !selectedUser || !days}
                                >
                                    {grantLoading ? (
                                        <>
                                            <div className="grant-btn-spinner"></div>
                                            Granting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-gift"></i>
                                            Grant Subscription
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminDashboardLayout>
    );
};

export default AdminSubscriptions;
